/**
 * Draw cut area for wall front view
 */

import { ActionState, ActionStateEnum, AreaState, AreaStateEditModeEnum, FrontViewState } from "..";
import { FrontViewManager } from "../../plan/FrontViewManager";
import { Command } from "../../../../constants";
import { PlanManager } from "../../../../cocosPlan";
import { AddArea, Commands, ComponentConstants, Entity, Events, eventsManager, math, SavaneConstants, SavaneMath, Segment } from "@rhinov/savane-js";

declare var cc: any;


export class FrontViewAddArea extends AreaState {

    _originalCursorPosition: math.vec3 | null = null;
    _openTulip: boolean = true;
    declare _area: AddArea;

    // Set current state enum
    state: number = ActionStateEnum.FrontViewAddArea;

    constructor(area: AddArea, editMode: number, cursorPosition: math.vec3) {
        super(editMode ? area : null, cursorPosition)
        // Store edition mode (create or edit)
        //this.editMode = editMode;
        // Record cursor position
        if (cursorPosition) {
            this._originalCursorPosition = math.vec3.clone(cursorPosition);
        }
        // Open tulip at the end
        
    }

    getAction(): number {
        return ActionStateEnum.FrontViewAddArea;
    }

    // Go to next state after the front view coating state (i.e. go back to front view state)
    nextState(): ActionState | null {
        PlanManager.getInstance().frontViewLayer.renderCurrentWall();
        eventsManager.instance.dispatch(Events.PLAN_STATE_CHANGED, ActionStateEnum.FrontView);
        return new FrontViewState(true);
    }

    isDecorationState(): boolean {
        return (false);
    }

    // Create a command to delete the current area (so it can be undone)
    deleteCommand(): Command {
        return new Commands.DeleteComponentCommand(this._area, true);
    }

    // Create a command to create a new area (so it can be undone)
    addCommand(): Command {
        return new Commands.AddComponentCommand(this._area, this._entity!, false, true);
    }

    // Transforms current cursor position (X, Z) into front view coordinates (X, Y)
    transformCursorPosition(cursorPosition: math.vec3): math.vec3 {
        var pos = math.vec3.clone(cursorPosition);
        pos[1] = pos[2];
        pos[2] = 0;
        return (pos);
    }

    // State initialisation
    init(area: AddArea, cursorPosition: math.vec3) {
        // Temporary variable to send modified cursor position (Z --> Y) to area state
        var pos: math.vec3 | null = null;

        if (cursorPosition) {
            pos = this.transformCursorPosition(cursorPosition);
        }

        // Call area state initialisation (it will create an area but we wil overwrite it below)
        super.init(area, pos as any, PlanManager.getInstance().frontViewLayer.currentWall!, undefined);

        // If we are creating a new coating area
        if (this.editMode === AreaStateEditModeEnum.new_area) {
            // Create a coating area for the area (and not simply an normal area as we need a coating info in addition to the standard fields of area object)
            this._area = new AddArea();
            // Store the entity owning the coating area
            this._area.entity = PlanManager.getInstance().frontViewLayer.currentWall;
            // Store the desired color index for the coating
            //this._area.colorIndex = this._coating.colorIndex !== undefined ? this._coating.colorIndex : this._coating.configs[0].index;
            // And if we have a cursorPosition pass to this method, store it as the first point of the area
            if (cursorPosition && pos) {
                this._area.vertices = [pos];
            }
            else {
                this._area.vertices = [];
            }
            // Store if direct side or not (side from begin to end or the other side)
            (this._area as any).isDirectSide = PlanManager.getInstance().frontViewLayer.isDirectSide;
        }
        else {
            FrontViewManager.selectDecoration(this._area as AddArea);
        }
    }

    // Draw the current CoatingArea
    drawArea(drawArea: number) {
        // If we are editing, set the area as selected to display it with a different color
        FrontViewManager.drawArea(this._area, drawArea, this.editMode, this._editedPointIndex, this._editedLineIndex);
    }

    // Delete the current CoatingArea
    deleteArea() {
        if (this._area.id !== null) {
            FrontViewManager.deleteArea(this._area.id);
        }
    }

    // Mouse button pressEd
    mouseClickDown(cursorPosition: math.vec3, doubleClick: boolean, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        // Modify cursor position (Z --> Y)
        var pos = this.transformCursorPosition(cursorPosition);
        // Call the AreaState method and store result (where to go)
        var state = super.mouseClickDown(pos, doubleClick, hoverEntities, mouseButton, true);
        switch (this.editMode) {
            case AreaStateEditModeEnum.area_selected:
            case AreaStateEditModeEnum.point_selected:
            case AreaStateEditModeEnum.line_selected:
                this._isEditing = true;
                break;
        }

        // Close any tulip here
        PlanManager.getInstance().updateItemsSelected(null, this._isEditing, false);

        // If edit mode is unknown then it means we have clicked outside of the current coating area
        if (this.editMode === AreaStateEditModeEnum.unknown) {
            // Current area isn't edited anymore, check all coating area
            var isDirectSide = PlanManager.getInstance().frontViewLayer.isDirectSide;
            var addAreas = PlanManager.getInstance().frontViewLayer.currentWall!.getComponents(ComponentConstants.ComponentType.AddArea) as AddArea[];

            // Parse them all
            for (var i = 0; i < addAreas.length; ++i) {
                // If one found with current direct side and the cursor position is in the polygon
                if (addAreas[i].isDirectSide === isDirectSide && SavaneMath.isInPoly(pos, addAreas[i].vertices)) {
                    // Unselect current coating area
                    FrontViewManager.unselectDecoration(this._area);
                    // Create a new view coating state with the new edited coating
                    return new FrontViewAddArea(addAreas[i], Number(true), cursorPosition);
                }
            }

            // No new coating area selected, go back to Front View
            return (this.nextState());
        }
        else {
            // Return result from Area State call
            return (state);
        }
    }

    // The mouse button is released
    mouseClickUp(cursorPosition: math.vec3, elapsedTime: number, hoverEntities: Entity[], mouseButton: number): ActionState | null {
        // Transform mouse cursor coordinates (Z --> Y)
        var pos = this.transformCursorPosition(cursorPosition);
        // If we are drawing points, let's delete it (it will be created again below)
        if (this._isDrawingPoint) {
            this.deleteArea();
        }

        this._openTulip = false;

        // Call area state method with the cursor position and store the result
        var state = super.mouseClickUp(pos, elapsedTime, hoverEntities, mouseButton);

        if (state === null) {
            // Draw the area
            this.drawArea(this.editMode);
            if (this._openTulip && this._entity) {
                PlanManager.getInstance().updateItemsSelected([this._entity], true, true);
            }
        } else if (this._isDrawingPoint === false && this._editedPointIndex === null) {
            return this.nextState();
        }

        // Go to the result of the area state call
        return state;
    }

    moveMouseCursor(cursorPosition: math.vec3, hoverEntities?: Entity[], planpos?: math.vec3): ActionState | null {
        // Do we have to open the tulip at the end of the current operation ?
        if (!this._originalCursorPosition) {
            this._originalCursorPosition = math.vec3.clone(cursorPosition);
        }

        // Change cursor pour to a vertical plane position (instead of horizontal)
        var pos = this.transformCursorPosition(cursorPosition);
        // Call AreaState method to know what to do and store result
        var toReturn = super.moveMouseCursor(pos);

        // Compute if the shape is valid
        this._isValid();

        // The the current area
        this.drawArea(this.editMode);
        // Return result of the call above
        return (toReturn);
    }

    keyPressed(keyCode: number, ctrl?: boolean, alt?: boolean): ActionState | null {
        if ((keyCode === cc.KEY.Delete || keyCode == cc.KEY.backspace)) {
            this.editMode = AreaStateEditModeEnum.unknown;
            this.drawArea(this.editMode);
        }
        var nextState = super.keyPressed(keyCode);
        // Page up and page down, flip the wall edited side
        if ((keyCode === 33) || (keyCode === 34)) {
            PlanManager.getInstance().frontViewLayer.toggleSide();
        }

        if (keyCode === cc.KEY.s) {
            PlanManager.getInstance().snapEnabled = !PlanManager.getInstance().snapEnabled;
            eventsManager.instance.dispatch(Events.SNAP_UPDATED);
        }

        if (keyCode === cc.KEY.a) {
            PlanManager.getInstance().magnetRotation = !PlanManager.getInstance().magnetRotation;
            eventsManager.instance.dispatch(Events.ANGLE_SNAP_UPDATED);
        }

        if (keyCode === cc.KEY.c) {
            return this._makeCircle();
        }

        if (keyCode === cc.KEY.e) {
            return this._makeElipse();
        }

        /*if (this._area.entity) {
            this.drawArea(this.editMode);
        }*/

        PlanManager.getInstance().frontViewLayer.renderCurrentWall(true);

        return nextState;
    }

    _makeCircle(): ActionState {
        if (this._area.vertices.length < 3) {
            return null as any;
        }

        var center = math.vec3.create();
        math.vec3.add(center, this._area.vertices[0], this._area.vertices[1]);
        math.vec3.divide(center, center, math.vec3.fromValues(2, 2, 2));
        var radius = math.vec3.distance(this._area.vertices[0], this._area.vertices[1]) / 2;
        var tanStep = 60;
        var angleStep = Math.atan(tanStep / radius);
        var newVertices = [];
        for (var step = 0; step < 2 * Math.PI; step += angleStep) {
            newVertices.push(math.vec3.fromValues(center[0] + radius * Math.cos(step), center[1] + radius * Math.sin(step), center[2]));
        }

        this.deleteArea();
        var newArea = this._area.clone();
        newArea.vertices = newVertices;
        PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(this._area, newArea, this._entity!));
        return this.nextState()!;
    }

    _makeElipse(): ActionState {
        if (this._area.vertices.length < 3) {
            return null as any;
        }

        var center = math.vec3.create();
        math.vec3.add(center, this._area.vertices[0], this._area.vertices[1]);
        math.vec3.divide(center, center, math.vec3.fromValues(2, 2, 2));
        var a = math.vec3.distance(this._area.vertices[0], this._area.vertices[1]) / 2;
        var b = math.vec3.distance(this._area.vertices[0], this._area.vertices[2]);
        var x = math.vec3.create();
        math.vec3.subtract(x, this._area.vertices[1], this._area.vertices[0]);
        math.vec3.normalize(x, x);
        var y = math.vec3.create();
        math.vec3.subtract(y, this._area.vertices[2], this._area.vertices[0]);
        math.vec3.normalize(y, y);
        var z = math.vec3.create();
        math.vec3.cross(z, y, x);
        math.vec3.cross(y, x, z);
        var frame = math.mat4.fromValues(
            x[0], x[1], x[2], 0,
            y[0], y[1], y[2], 0,
            z[0], z[1], z[2], 0,
            center[0], center[1], center[2], 1);
        var tanStep = 60;
        var angleStep = Math.atan(tanStep / a);
        var newVertices = [];
        for (var step = 0; step < 2 * Math.PI; step += angleStep) {
            var position = math.vec3.fromValues(a * Math.cos(step), b * Math.sin(step), 0);
            math.vec3.transformMat4(position, position, frame);
            newVertices.push(position);
        }

        this.deleteArea();
        var newArea = this._area.clone();
        newArea.vertices = newVertices;
        PlanManager.getInstance().executeCommand(new Commands.ReplaceComponentCommand(this._area, newArea, this._entity!));
        return this.nextState()!;
    }

    // Is the area shape valid
    _isValid(): boolean{
        var i, j,
            vertices1 = this._area.vertices,
            segment1,
            segment2;

        // Current area has less than 3 points but still drawing (--> Ok)
        if (this._area.vertices.length < 3 && this._isDrawingPoint) {
            FrontViewManager.setAreaValidity(this._area, true);
            return true;
        } else if (this._area.vertices.length > 2) {
            for (i = 0; i < this._area.vertices.length; ++i) {
                var current = this._area.vertices[i];
                var next = this._area.vertices[(i + 1) % this._area.vertices.length];
                var second = this._area.vertices[(i + 2) % this._area.vertices.length];
                var segment = new Segment(current, second);
                    if (segment.isPointOnSegment(next, 1) === true) {
                    FrontViewManager.setAreaValidity(this._area, false);
                    return false;
                }
            }
        }

        // Segment of the area can not cross each other
        for (i = 0; i < vertices1.length - (this._isDrawingPoint ? 1 : 0); i++) {
            // Create a segment with polygon current point and next point
            segment1 = new Segment(vertices1[i], vertices1[(i + 1) % vertices1.length]);

            // Segment too small, invalid area
            if (segment1.length < SavaneConstants.PositionTolerance) {
                FrontViewManager.setAreaValidity(this._area, false);
                return false;
            }

            // Parse all other vertices and check segments aren't crossing
            for (j = 0; j < vertices1.length; j++) {
                //do not test same segment and adjacent one
                if (Math.abs(i - j) > 1 && !(i === 0 && j === vertices1.length - 1) && !(j === 0 && i === vertices1.length - 1)) {
                    // Create a second segment with the next 2 points
                    segment2 = new Segment(vertices1[j], vertices1[(j + 1) % vertices1.length]);
                    if (Segment.areSegmentCrossing(segment1, segment2, SavaneConstants.PositionTolerance) && !(this._isDrawingPoint && (i === vertices1.length - 1 || j === vertices1.length - 1))) {
                        FrontViewManager.setAreaValidity(this._area, false);
                        return false;
                    }
                }
            }
        }

        // If reach here, then the shape is valid
        FrontViewManager.setAreaValidity(this._area, true);
        return true;
    }
}
